import * as Yup from 'yup';
import { calculateAge } from '@shared/helpers/calculateAge';
import isValidDate from '@shared/helpers/isValidDate';

// Mismos patrones que valida el back (api-rules-engine, validations/assist-card.js)
const defaultDocumentNumberRule = {
  pattern: /^[0-9a-zA-Zñ]+$/,
  message: 'El documento solo puede contener letras y números',
};

const documentNumberRules = {
  DNI: { pattern: /^\d+$/, message: 'El DNI solo puede contener números' },
};

// --- Schema para pasajeros ---
export const passengerSchema = elementSelected =>
  Yup.array().of(
    Yup.object().shape({
      name: Yup.string()
        .matches(/^[a-zA-Z\s]+$/, 'El nombre contiene caracteres inválidos')
        .required('El nombre es requerido'),

      last_name: Yup.string()
        .matches(/^[a-zA-Z\s]+$/, 'El apellido contiene caracteres inválidos')
        .required('El apellido es requerido'),

      birthdate: Yup.string()
        .required('La fecha de nacimiento es obligatoria')
        .test('valid-date', 'Debe elegir una fecha válida', value =>
          isValidDate(value)
        )
        .test(
          'age-match',
          'No coincide con las fechas de búsqueda y las ingresadas',
          function (value) {
            if (!value || !isValidDate(value)) return true;

            const index = parseInt(this.path.match(/\d+/)?.[0], 10);
            const expected =
              elementSelected?.clientesCotizados?.clienteCotizacion?.[index]
                ?.fechaNacimiento;

            if (!expected) return true;

            const inputAge = calculateAge(value);
            const expectedAge = calculateAge(expected);

            // determinar rango de edad
            const getRange = age => {
              if (age < 70) return 'm70';
              if (age < 75) return 'm75';
              return 'ms75';
            };

            if (getRange(inputAge) !== getRange(expectedAge)) {
              return this.createError({
                path: this.path,
                message:
                  'La edad indicada no coincide con el rango seleccionado',
              });
            }

            return true;
          }
        ),
      document_type: Yup.string().required('El tipo de documento es requerido'),

      document_number: Yup.string()
        .required('El documento es requerido')
        .test('doc-pattern', function (value) {
          const rule =
            documentNumberRules[this.parent.document_type] ||
            defaultDocumentNumberRule;

          if (rule.pattern.test((value || '').trim())) return true;

          return this.createError({ message: rule.message });
        })
        .test('doc-length', 'Número de documento inválido', function (value) {
          const type = this.parent.document_type;
          const len = value?.toString().length;
          if (type === 'DNI') return len >= 7 && len <= 8;
          return len >= 5 && len <= 20;
        }),

      country: Yup.string().required('El país es requerido'),

      // 👇 VALIDACIONES DEL PRIMER PASAJERO
      phone: Yup.string()
        .test('required-first-passenger', 'El teléfono es requerido', function (
          value
        ) {
          const index = parseInt(this.path.match(/\d+/)?.[0]);
          return index === 0 ? !!value : true; // solo el primero es obligatorio
        })
        .test(
          'max-length-first-passenger',
          'El teléfono no puede superar 20 caracteres',
          function () {
            const index = parseInt(this.path.match(/\d+/)?.[0]);
            if (index !== 0) return true; // solo validar el primer pasajero

            const {
              codePhone = '',
              caracteristicPhone = '',
              phone = '',
            } = this.parent;

            // concatenamos y eliminamos espacios
            const totalLength = (
              codePhone +
              caracteristicPhone +
              phone
            ).replace(/\s+/g, '').length;

            return totalLength <= 20;
          }
        ),

      codePhone: Yup.string().test(
        'required-first-passenger-code',
        'El código es requerido',
        function (value) {
          const index = parseInt(this.path.match(/\d+/)?.[0]);
          return index === 0 ? !!value : true;
        }
      ),

      caracteristicPhone: Yup.string().test(
        'required-first-passenger-caracteristic',
        'Campo requerido',
        function (value) {
          const index = parseInt(this.path.match(/\d+/)?.[0]);
          return index === 0 ? !!value : true;
        }
      ),
    })
  );

// --- Schema para NoViaja ---
export const noViajaSchema = Yup.object().shape({
  name: Yup.string()
    .matches(/^[a-zA-Z\s]+$/, 'El nombre contiene caracteres inválidos')
    .required('El nombre es requerido'),

  lastname: Yup.string()
    .matches(/^[a-zA-Z\s]+$/, 'El apellido contiene caracteres inválidos')
    .required('El apellido es requerido'),

  phone: Yup.string().required('El teléfono es requerido'),
  codePhone: Yup.string().required('Campo requerido'),
  caracteristicPhone: Yup.string().required('Campo requerido'),
});

// --- Schema para Voucher ---
export const contactSchema = Yup.object().shape({
  email: Yup.string()
    .email('El formato del email es incorrecto')
    .required('El email es requerido'),

  repeatEmail: Yup.string()
    .oneOf([Yup.ref('email'), null], 'Los emails deben coincidir')
    .required('Repetir email es obligatorio'),
});

// --- Schema combinado para Formik ---
export const fullSchema = elementSelected =>
  Yup.object().shape({
    passengers: passengerSchema(elementSelected),
    noViaja: noViajaSchema,
    contact: contactSchema,

    acceptedTerms: Yup.boolean().oneOf([true], 'Debes aceptar los términos'),

    id_entity: Yup.string().test(
      'required-if-pos-and-emit',
      'Debes seleccionar un punto de venta',
      function (value) {
        const { isPointOfSale, isEmit } = this.parent;
        if (!isEmit) return true;
        return isPointOfSale ? !!value : true;
      }
    ),

    id_assigned_seller: Yup.string().test(
      'required-if-seller-and-emit',
      'Debes seleccionar un vendedor',
      function (value) {
        const { isPointOfSale, isEmit } = this.parent;
        if (!isEmit) return true;
        return isPointOfSale ? true : !!value;
      }
    ),
  });
